import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getKeys } from "../../apis/actions/moyasar.actions";
import { RootState } from "../../apis/store";
import DotsLoader from "../../biblio/Loader/DotsLoader";
import ModalComponent from "../../biblio/Modal/ModalComponent";
import { useCookies } from "react-cookie";

interface MoyasarProps {
  amount: number;
  description?: string;
  open: boolean;
  handleClose: () => void;
}

const MoyasarForm = ({
  amount,
  description,
  open,
  handleClose,
}: MoyasarProps) => {
  const moyasar = useSelector((state: RootState) => state?.moyasar);
  const [cookies] = useCookies(["role"]);
  const otpId = useSelector((state: RootState) => state?.recipient?.OtpId);

  const moyasarRef = useRef<HTMLDivElement>(null);
  const [key, setKey] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const sponsor = useSelector(
    (state: RootState) => state?.sponsor?.sponsorDetails
  );

  useEffect(() => {
    getKeys().then((res: any) => {
      setKey(res.default_publishable_api_key);
    });
  }, []);

  const buildMetadata = () => {
    const metadata: Record<string, any> = {};

    // Mobile
    let mobile: string = "";
    if (moyasar.mobile && moyasar.mobile !== "") {
      mobile = moyasar.mobile;
    } else if (sponsor && sponsor?.mobile && cookies?.role) {
      mobile = sponsor?.mobile;
    }
    if (mobile) metadata.mobile = mobile;
    if (moyasar.donner_name?.trim()) {
      metadata.donner_name = moyasar.donner_name.trim();
    }

    // Donation type
    if (moyasar.donation_type_id)
      metadata.donation_type_id = moyasar.donation_type_id;
    if (moyasar.donation_type_name)
      metadata.donation_type_name = moyasar.donation_type_name;

    // Model-based logic
    if (moyasar.model_id) {
      const model = moyasar.model;
      const model_id = moyasar.model_id;

      // eslint-disable-next-line eqeqeq
      if (model == "3") {
        metadata.project_id = model_id;
      // eslint-disable-next-line eqeqeq
      } else if (model == "4") {
        metadata.general_donation_id = model_id;
      // eslint-disable-next-line eqeqeq
      } else if (model == "1" || model == "2") {
        metadata.korba_id = model_id;
      }
      metadata.model_id = model_id;
    }

    // Keep model in metadata if it exists and wasn't already set
    if (moyasar.model && !metadata.model) {
      metadata.model = moyasar.model;
    }

    // Type
    if (moyasar.type) metadata.type = moyasar.type;

    // Gift properties
    const gift = moyasar.gift;
    if (gift) {
      if (gift.sender_name) metadata.sender_name = gift.sender_name;
      if (gift.card_type_id) metadata.card_type_id = gift.card_type_id;
      if (gift.name) metadata.name = gift.name;
      if (gift.mobile_number) metadata.mobile_number = gift.mobile_number;
      if (gift.send_copy_to_sender !== undefined)
        metadata.send_copy_to_sender = gift.send_copy_to_sender;
      if (gift.show_name_in_dedication !== undefined)
        metadata.show_name_in_dedication = gift.show_name_in_dedication;
      if (gift.show_amount !== undefined)
        metadata.show_amount = gift.show_amount;
      if (gift.send_gift !== undefined) metadata.send_gift = gift.send_gift;
    }

    // Sponsorship
    if (moyasar.sponsorship_booking_id)
      metadata.sponsorship_booking_id = moyasar.sponsorship_booking_id;
    if (moyasar.is_sponsorship_pay)
      metadata.is_sponsorship_pay = moyasar.is_sponsorship_pay;
    if (moyasar.month_number)
      metadata.month_number = String(moyasar.month_number);
    if (moyasar.renew_all) metadata.renew_all = moyasar.renew_all;

    // Program
    if (moyasar.program_application_id)
      metadata.program_application_id = moyasar.program_application_id;

    // Arrays - only include if not empty
    if (moyasar.items && moyasar.items.length > 0) {
      metadata.payment_beneficiary_service_ids = JSON.stringify(moyasar.items);
    }
    if (moyasar.beneficiary_lines && moyasar.beneficiary_lines.length > 0) {
      metadata.beneficiary_lines = JSON.stringify(moyasar.beneficiary_lines);
    }
    if (moyasar.participants && moyasar.participants.length > 0) {
      metadata.participants = JSON.stringify(moyasar.participants);
    }
    if (moyasar.bookingLines && moyasar.bookingLines.length > 0) {
      metadata.booking_lines = JSON.stringify(moyasar.bookingLines);
    }
    if (moyasar.donationLines && moyasar.donationLines.length > 0) {
      metadata.donation_amount_line_ids = JSON.stringify(moyasar.donationLines);
    }

    // Partner ID
    if (otpId && otpId !== -1) {
      metadata.partner_id = otpId;
    }
    metadata.takaful_website = true;

    return metadata;
  };
  useEffect(() => {
    if (!open) return;
    const initMoyasar = () => {
      if (moyasarRef.current && window.Moyasar && key) {
        moyasarRef.current.innerHTML = "";
        const metadata = buildMetadata();
        localStorage.setItem("moyasar_metadata", JSON.stringify(metadata));
        const callbackUrl = new URL(`${window.location.origin}/donation`);
        if (moyasar.donation_type_name) {
          callbackUrl.searchParams.set(
            "donation_type_name",
            moyasar.donation_type_name
          );
        }
        if (metadata.donner_name) {
          callbackUrl.searchParams.set("donner_name", metadata.donner_name);
        }
        callbackUrl.searchParams.set("amountPaid", String(amount));

        window.Moyasar.init({
          element: moyasarRef.current,
          amount: amount * 100,
          currency: "SAR",
          description: description ?? "",
          publishable_api_key: key,
          callback_url: callbackUrl.toString(),
          methods: ["creditcard", "applepay"],
          apple_pay: {
            country: "SA",
            label: "Takaful",
            validate_merchant_url:
              "https://api.moyasar.com/v1/applepay/initiate",
          },
          language: "ar",
          metadata,
          credit_card: {
            save_card: false,
          },
        });
        setIsLoading(false);
      }
    };
    initMoyasar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moyasarRef, key, window.Moyasar, open, amount, moyasar]);
  return (
    <ModalComponent
      title=""
      open={open}
      onClose={handleClose}
      closeOnOutsideClick={true}
      className="takaful--moyassar-modal"
    >
      {isLoading && (
        <div
          className="takaful--loading-projects-cases"
          style={{ minHeight: "20vh" }}
        >
          <p className="takaful--no-project-message"> جارٍ التحميل</p>
          <DotsLoader />
        </div>
      )}
      <div ref={moyasarRef} style={{ direction: "rtl" }} />
    </ModalComponent>
  );
};

export default MoyasarForm;
